/**
 * CreativeStudioSection — AI ad-copy generation + review workflow inside
 * the campaign detail page. Generate (angle matrix; the run continues in
 * the background — the panel shows it writing, polls the generation list
 * and reports the outcome when it ends) → review draft
 * creatives (critique scores shown) → approve/reject → deploy into a
 * placement's channel ad group, optionally binding an ad_creative
 * experiment variant. Challenger generation feeds observed performance
 * back into the prompt server-side. When the campaign has landing-page
 * variants, copy is generated for one of them (its URL + brief) and each
 * creative shows the variant it lands on.
 */

import { useState } from 'react';
import type {
  AdoptCreativeDto,
  AdoptGenerationDto,
  AdoptKeywordIdeaDto,
  AdoptLandingVariantDto,
  AdoptPlacementDto,
} from '@/lib/api';
import {
  useAdoptCreativeAction,
  useAdoptCreatives,
  useAdoptGenerations,
  useAdoptPlacements,
  useDeployAdoptCreative,
  useGenerateAdoptChallengers,
  useGenerateAdoptCopy,
  useGenerateAdoptKeywords,
  useKillAdoptCreative,
} from '../hooks/useAdvertisingOptimization';
import { CreativeEditor } from './CreativeEditor';
import { AdPreview, HowAdsWork } from './AdPreview';
import { DeployButton, noDeployablePlacementNote } from './CreativeDeployActions';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

interface Props {
  holderId: string;
  campaignId: string;
}

interface StudioProps extends Props {
  /** The campaign's `promotedTarget.landingVariants` (absent/empty = one landing page). */
  landingVariants?: AdoptLandingVariantDto[];
  /** The campaign's banned claims (copy facts) — flagged live while editing an ad. */
  bannedClaims?: readonly string[];
}

/** Statuses a person may edit (the server's PATCH allows exactly these). */
const EDITABLE_STATUSES = ['draft', 'pending_approval', 'rejected'];

const ANGLES = [
  'feature_led',
  'pain_led',
  'social_proof',
  'price_led',
  'urgency',
  'curiosity',
] as const;

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  approved: 'bg-sky-100 text-sky-800',
  deploying: 'bg-amber-100 text-amber-800',
  live: 'bg-emerald-100 text-emerald-800',
  paused: 'bg-amber-100 text-amber-800',
  killed: 'bg-rose-100 text-rose-800',
  rejected: 'bg-rose-100 text-rose-800',
  error: 'bg-rose-100 text-rose-800',
};

/** The outcome line for a finished generation. */
function generationOutcome(g: AdoptGenerationDto): string {
  if (g.status === 'failed') return `Generation failed: ${g.error ?? 'unknown error'}`;
  const accepted = g.summary?.accepted ?? g.acceptedCreativeIds.length;
  const rejected = g.summary?.rejected ?? g.rejectedCount;
  const failed = g.summary?.failed ?? 0;
  return [
    `${accepted} draft${accepted === 1 ? '' : 's'} created`,
    rejected > 0 ? `${rejected} rejected by checks` : null,
    failed > 0 ? `${failed} failed to generate` : null,
  ]
    .filter(Boolean)
    .join(', ')
    .concat('.');
}

/** "feature led, pain led" — the angles a generation was asked for. */
function angleList(angles: readonly string[]): string {
  return angles.map((a) => a.replace('_', ' ')).join(', ');
}

/** Same angle set (order-insensitive). */
function sameAngles(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
}

/** How many of this panel's finished runs keep their outcome line on screen. */
const OUTCOMES_SHOWN = 3;

function GeneratePanel({
  holderId,
  campaignId,
  landingVariants = [],
  generations,
}: StudioProps & { generations: AdoptGenerationDto[] }) {
  const generateCopy = useGenerateAdoptCopy(holderId, campaignId);
  const generateChallengers = useGenerateAdoptChallengers(holderId, campaignId);
  const [selectedAngles, setSelectedAngles] = useState<string[]>(['feature_led', 'pain_led']);
  const [instructions, setInstructions] = useState('');
  // '' = the base landing page. Defaults to the first variant when there are
  // any, so a variant campaign does not silently generate for the base URL.
  const [landingVariantKey, setLandingVariantKey] = useState<string>(landingVariants[0]?.key ?? '');
  // Generations started from this panel, newest first — their outcome is
  // reported here when they finish.
  const [startedIds, setStartedIds] = useState<string[]>([]);

  const toggleAngle = (angle: string): void => {
    setSelectedAngles((prev) =>
      prev.includes(angle) ? prev.filter((a) => a !== angle) : [...prev, angle],
    );
  };

  const remember = (id: string): void => setStartedIds((prev) => [id, ...prev]);

  const running = generations.filter((g) => g.status === 'running');
  // The same request is already being written: same angles, same landing page.
  const sameCopyRunning = running.some(
    (g) =>
      g.kind === 'copy' &&
      sameAngles(g.angles, selectedAngles) &&
      (g.landingVariantKey ?? '') === landingVariantKey,
  );
  const challengersRunning = running.some((g) => g.kind === 'challengers');
  const finished = startedIds
    .map((id) => generations.find((g) => g.id === id))
    .filter((g): g is AdoptGenerationDto => !!g && g.status !== 'running')
    .slice(0, OUTCOMES_SHOWN);

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <h3 className="font-medium">Generate ad copy</h3>
      <div className="flex flex-wrap gap-2">
        {ANGLES.map((angle) => (
          <button
            key={angle}
            onClick={() => toggleAngle(angle)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              selectedAngles.includes(angle)
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {angle.replace('_', ' ')}
          </button>
        ))}
      </div>
      {landingVariants.length > 0 && (
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Landing page</span>
          <select
            aria-label="Landing page variant"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={landingVariantKey}
            onChange={(e) => setLandingVariantKey(e.target.value)}
          >
            {landingVariants.map((v) => (
              <option key={v.key} value={v.key}>
                {v.key} (/{v.path})
              </option>
            ))}
            <option value="">Base landing page</option>
          </select>
        </label>
      )}
      <textarea
        rows={2}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        placeholder="Optional instructions (e.g. lean into the free tier)"
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
      />
      <div className="flex gap-2">
        <button
          onClick={() =>
            generateCopy.mutate(
              {
                angles: selectedAngles,
                instructions: instructions.trim() || undefined,
                landingVariantKey: landingVariantKey || undefined,
              },
              { onSuccess: (data) => remember(data.generation.id) },
            )
          }
          disabled={generateCopy.isPending || selectedAngles.length === 0 || sameCopyRunning}
          title={sameCopyRunning ? 'Already writing ads for these angles' : undefined}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          {generateCopy.isPending ? 'Starting…' : 'Generate copy'}
        </button>
        <button
          onClick={() =>
            generateChallengers.mutate({}, { onSuccess: (data) => remember(data.generation.id) })
          }
          disabled={generateChallengers.isPending || challengersRunning}
          title="Generates new variants informed by the performance of ads already running"
          className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-50"
        >
          {generateChallengers.isPending ? 'Starting…' : 'Generate challengers'}
        </button>
      </div>
      {(generateCopy.isError || generateChallengers.isError) && (
        <div className="text-sm text-rose-600 dark:text-rose-400" role="alert">
          {((generateCopy.error ?? generateChallengers.error) as Error | null)?.message ??
            'Generation failed'}
        </div>
      )}
      {running.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm" aria-live="polite">
          {running.map((g) => (
            <li key={g.id} className="flex items-center gap-2 text-muted-foreground">
              <span
                aria-hidden
                className="inline-block h-3 w-3 rounded-full border-2 border-primary border-t-transparent animate-spin"
              />
              <span>
                {g.kind === 'challengers' ? 'Writing challengers' : 'Writing ads'}
                {g.angles.length > 0 ? ` (${angleList(g.angles)})` : ''}
                {g.landingVariantKey ? ` for ${g.landingVariantKey}` : ''}… usually 30–60 seconds.
              </span>
            </li>
          ))}
        </ul>
      )}
      {finished.length > 0 && (
        <ul className="flex flex-col gap-1 text-sm" aria-live="polite">
          {finished.map((g) => (
            <li
              key={g.id}
              className={
                g.status === 'failed' ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'
              }
            >
              {g.angles.length > 0 ? `${angleList(g.angles)}: ` : ''}
              {generationOutcome(g)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KeywordsPanel({ holderId, campaignId }: Props) {
  const generateKeywords = useGenerateAdoptKeywords(holderId, campaignId);
  const [seed, setSeed] = useState('');
  const keywords: AdoptKeywordIdeaDto[] = generateKeywords.data?.keywords ?? [];

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <h3 className="font-medium">Keyword ideas</h3>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          placeholder="Optional seed terms, comma-separated"
          value={seed}
          onChange={(e) => setSeed(e.target.value)}
        />
        <button
          onClick={() =>
            generateKeywords.mutate({
              seedTerms: seed
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={generateKeywords.isPending}
          className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-50"
        >
          {generateKeywords.isPending ? 'Thinking…' : 'Suggest keywords'}
        </button>
      </div>
      {generateKeywords.isError && (
        <div className="text-sm text-rose-600">Keyword generation failed — try again.</div>
      )}
      {keywords.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {keywords.map((k, i) => (
            <li
              key={i}
              className={`text-xs rounded px-1.5 py-0.5 border ${
                k.negative
                  ? 'border-rose-200 text-rose-700 line-through'
                  : 'border-border text-foreground'
              }`}
              title={k.negative ? 'negative keyword' : k.matchType}
            >
              {k.text} <span className="text-muted-foreground">({k.matchType})</span>
            </li>
          ))}
        </ul>
      )}
      {keywords.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Copy the useful ones into a placement's keyword list — struck-through terms are suggested
          negatives.
        </p>
      )}
    </div>
  );
}

function CreativeCard({
  creative,
  holderId,
  campaignId,
  placements,
  bannedClaims,
}: {
  creative: AdoptCreativeDto;
  holderId: string;
  campaignId: string;
  placements: AdoptPlacementDto[];
  bannedClaims: readonly string[];
}) {
  const action = useAdoptCreativeAction(holderId, campaignId);
  const deploy = useDeployAdoptCreative(holderId, campaignId);
  const kill = useKillAdoptCreative(holderId, campaignId);
  const [editing, setEditing] = useState(false);
  // Inline kill confirmation (never a native prompt: an empty or suppressed
  // window.prompt silently did nothing). null = closed.
  const [killReason, setKillReason] = useState<string | null>(null);
  // Which step of a deploy / approve-and-deploy was refused, and why.
  const [stepError, setStepError] = useState<string | null>(null);
  const busy = action.isPending || deploy.isPending;

  const deployTo = async (placementId: string): Promise<void> => {
    setStepError(null);
    try {
      await deploy.mutateAsync({ creativeId: creative.id, placementId });
    } catch (err) {
      setStepError(`Not deployed: ${errorMessage(err, 'the deploy failed')}`);
    }
  };

  // Approve, then deploy — the same two routes, stopping at the first refusal.
  const approveAndDeploy = async (placementId: string): Promise<void> => {
    setStepError(null);
    try {
      await action.mutateAsync({ creativeId: creative.id, verb: 'approve' });
    } catch (err) {
      setStepError(`Not approved, so not deployed: ${errorMessage(err, 'approval failed')}`);
      return;
    }
    try {
      await deploy.mutateAsync({ creativeId: creative.id, placementId });
    } catch (err) {
      setStepError(
        `Approved, but not deployed: ${errorMessage(err, 'the deploy failed')} It stays approved — deploy it again when that is resolved.`,
      );
    }
  };
  const deployNote = ['draft', 'pending_approval', 'approved', 'error'].includes(creative.status)
    ? noDeployablePlacementNote(placements)
    : null;
  const [previewing, setPreviewing] = useState(false);
  const scores = creative.critique?.scores;
  const editable = EDITABLE_STATUSES.includes(creative.status);

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={`text-xs rounded px-1.5 py-0.5 ${STATUS_STYLES[creative.status] ?? 'bg-muted'}`}
          >
            {creative.status}
          </span>
          {/* Same format as the generation history, so an ad can be matched to its run. */}
          <time dateTime={creative.createdAt} className="text-xs text-muted-foreground">
            created {new Date(creative.createdAt).toLocaleString()}
          </time>
          <span
            className="text-xs text-muted-foreground"
            title="The angle this ad's own copy leads with — not the landing page's"
          >
            angle: {creative.angle.replace('_', ' ')}
          </span>
          <span className="text-xs text-muted-foreground">
            {creative.createdBy === 'ai' ? 'AI-generated' : 'manual'}
          </span>
          {creative.content.landingVariantKey && (
            <span
              className="text-xs rounded px-1.5 py-0.5 border border-border"
              title={creative.content.finalUrl}
            >
              lands on: {creative.content.landingVariantKey}
            </span>
          )}
        </div>
        <div className="flex gap-1.5">
          {!editing && (
            <button
              onClick={() => setPreviewing((p) => !p)}
              aria-expanded={previewing}
              className="px-2.5 py-1 rounded-md border border-border text-xs"
            >
              {previewing ? 'Hide preview' : 'Preview'}
            </button>
          )}
          {editable && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="px-2.5 py-1 rounded-md border border-border text-xs"
            >
              Edit
            </button>
          )}
          {['draft', 'pending_approval'].includes(creative.status) && !editing && (
            <>
              <button
                onClick={() => {
                  setStepError(null);
                  action.mutate({ creativeId: creative.id, verb: 'approve' });
                }}
                disabled={busy}
                className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => {
                  setStepError(null);
                  action.mutate({ creativeId: creative.id, verb: 'reject' });
                }}
                disabled={busy}
                className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
              >
                Reject
              </button>
              <DeployButton
                label="Approve & deploy"
                placements={placements}
                preferredPlacementId={creative.placementId}
                onDeploy={(placementId) => void approveAndDeploy(placementId)}
                pending={busy}
              />
            </>
          )}
          {(creative.status === 'approved' || creative.status === 'error') && (
            <DeployButton
              label={creative.status === 'error' ? 'Retry deploy' : 'Deploy'}
              placements={placements}
              preferredPlacementId={creative.placementId}
              onDeploy={(placementId) => void deployTo(placementId)}
              pending={busy}
            />
          )}
          {creative.status === 'live' && (
            <button
              onClick={() => action.mutate({ creativeId: creative.id, verb: 'pause' })}
              disabled={action.isPending}
              className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
            >
              Pause
            </button>
          )}
          {creative.status === 'paused' && (
            <button
              onClick={() => action.mutate({ creativeId: creative.id, verb: 'resume' })}
              disabled={action.isPending}
              className="px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs disabled:opacity-50"
            >
              Resume
            </button>
          )}
          {['live', 'paused'].includes(creative.status) && (
            <button
              onClick={() => setKillReason((open) => (open === null ? '' : null))}
              disabled={kill.isPending}
              aria-expanded={killReason !== null}
              className="px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 text-xs disabled:opacity-50"
            >
              Kill
            </button>
          )}
        </div>
      </div>

      {killReason !== null && ['live', 'paused'].includes(creative.status) && (
        <form
          className="rounded-md border border-rose-300 dark:border-rose-800 p-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            kill.mutate(
              {
                creativeId: creative.id,
                reason: killReason.trim() || 'Stopped in Creative Studio',
              },
              { onSuccess: () => setKillReason(null) },
            );
          }}
        >
          <p className="text-sm font-medium">Stop this ad for good?</p>
          <p className="text-xs text-muted-foreground">
            It is removed from rotation in Google and can&apos;t be resumed. Use Pause if you might
            want it back.
          </p>
          <label className="block text-xs">
            <span className="text-muted-foreground">Reason (optional)</span>
            <input
              type="text"
              value={killReason}
              maxLength={500}
              onChange={(e) => setKillReason(e.target.value)}
              className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm"
              placeholder="e.g. Makes a claim we don't stand behind"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={kill.isPending}
              className="px-2.5 py-1 rounded-md bg-rose-600 text-white text-xs disabled:opacity-50"
            >
              {kill.isPending ? 'Stopping…' : 'Kill ad'}
            </button>
            <button
              type="button"
              onClick={() => setKillReason(null)}
              className="px-2.5 py-1 rounded-md border text-xs"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {deployNote && <p className="text-xs text-muted-foreground">{deployNote}</p>}

      {previewing && !editing && <AdPreview content={creative.content} />}

      {editing ? (
        <CreativeEditor
          holderId={holderId}
          campaignId={campaignId}
          creative={creative}
          bannedClaims={bannedClaims}
          onDone={() => setEditing(false)}
        />
      ) : (
        <div className="text-sm">
          <div className="font-medium">
            {creative.content.headlines.map((h) => h.text).join(' · ')}
          </div>
          <div className="text-muted-foreground mt-1">
            {creative.content.descriptions.map((d) => d.text).join(' ')}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {scores && (
          <span title="AI critique scores: compliance / distinctiveness / landing match">
            critique {scores.compliance.toFixed(1)} / {scores.distinctiveness.toFixed(1)} /{' '}
            {scores.landingMatch.toFixed(1)}
          </span>
        )}
        {creative.externalAdRef && <span>channel ad {creative.externalAdRef}</span>}
        {creative.killedReason && <span className="text-rose-600">{creative.killedReason}</span>}
      </div>
      {(creative.critique?.claimViolations?.length ?? 0) > 0 && (
        <div className="text-xs text-amber-700 dark:text-amber-400">
          Reviewer flagged against your copy facts: {creative.critique.claimViolations!.join(' · ')}
        </div>
      )}
      {stepError ? (
        <div className="text-xs text-rose-600 dark:text-rose-400" role="alert">
          {stepError}
        </div>
      ) : (
        (action.isError || kill.isError) && (
          <div className="text-xs text-rose-600 dark:text-rose-400" role="alert">
            {errorMessage(action.error ?? kill.error, 'Action failed')}
          </div>
        )
      )}
    </div>
  );
}

export function CreativeStudioSection({
  holderId,
  campaignId,
  landingVariants,
  bannedClaims = [],
}: StudioProps) {
  const creativesQuery = useAdoptCreatives(holderId, campaignId);
  const placementsQuery = useAdoptPlacements(holderId, campaignId);
  const generationsQuery = useAdoptGenerations(holderId, campaignId);
  const [showHistory, setShowHistory] = useState(false);

  const creatives = creativesQuery.data ?? [];

  return (
    <section className="flex flex-col gap-4">
      <GeneratePanel
        holderId={holderId}
        campaignId={campaignId}
        landingVariants={landingVariants}
        generations={generationsQuery.data ?? []}
      />
      <KeywordsPanel holderId={holderId} campaignId={campaignId} />

      <HowAdsWork />

      <div className="flex items-center justify-between">
        <h2 className="font-medium">Creatives ({creatives.length})</h2>
        <button
          onClick={() => setShowHistory((s) => !s)}
          className="text-sm text-primary hover:underline"
        >
          {showHistory ? 'Hide generation history' : 'Generation history'}
        </button>
      </div>

      {showHistory && (
        <div className="rounded-lg border border-border p-4 text-sm">
          {(generationsQuery.data ?? []).length === 0 ? (
            <p className="text-muted-foreground">No generations yet.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {(generationsQuery.data ?? []).map((g) => (
                <li key={g.id} className="flex items-center gap-2">
                  <span className="font-medium">{g.kind}</span>
                  <span className="text-muted-foreground">
                    {new Date(g.createdAt).toLocaleString()} · {g.model} ·{' '}
                    {g.status === 'running'
                      ? 'writing…'
                      : `${g.acceptedCreativeIds.length} accepted, ${g.rejectedCount} rejected`}
                  </span>
                  {g.error && <span className="text-rose-600 text-xs">{g.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {creativesQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading creatives…</p>
      ) : creatives.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No creatives yet — generate a batch above to get started.
        </p>
      ) : (
        creatives.map((c) => (
          <CreativeCard
            key={c.id}
            creative={c}
            holderId={holderId}
            campaignId={campaignId}
            placements={placementsQuery.data ?? []}
            bannedClaims={bannedClaims}
          />
        ))
      )}
    </section>
  );
}
